import { UserProfile } from "../types";

export interface ProgressEntry {
  id: string;
  date: Date;
  handicap?: number;
  clubDistances?: { [club: string]: number };
  notes?: string;
  achievements?: string[];
}

export interface PracticeRecommendation {
  category: "driving" | "irons" | "short-game" | "putting" | "mental";
  title: string;
  description: string;
  duration: string;
  frequency: string;
  difficulty: "beginner" | "intermediate" | "advanced";
  tips: string[];
}

const PROGRESS_STORAGE_KEY = "clubiq-progress";

export const saveProgressEntry = (entry: ProgressEntry): void => {
  try {
    const existing = getProgressHistory();
    const updated = [...existing, entry];
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Error saving progress:", error);
  }
};

export const getProgressHistory = (): ProgressEntry[] => {
  try {
    const stored = localStorage.getItem(PROGRESS_STORAGE_KEY);
    if (stored) {
      const entries = JSON.parse(stored);
      return entries.map((entry: any) => ({
        ...entry,
        date: new Date(entry.date),
      }));
    }
  } catch (error) {
    console.error("Error loading progress:", error);
  }
  return [];
};

export const generatePracticeRecommendations = (
  userProfile: UserProfile,
): PracticeRecommendation[] => {
  const recommendations: PracticeRecommendation[] = [];
  const skillLevel = userProfile.skillLevel;
  const handicap = userProfile.handicap;
  const issues = userProfile.currentIssues;

  // Driving recommendations
  if (
    issues.includes("Driving accuracy") ||
    issues.includes("Driving distance")
  ) {
    recommendations.push({
      category: "driving",
      title: "Alignment and Setup Practice",
      description:
        "Use alignment sticks to practice proper setup and swing path",
      duration: "20 minutes",
      frequency: "3x per week",
      difficulty: skillLevel,
      tips: [
        "Place alignment stick parallel to target line",
        "Practice with feet, hips, and shoulders aligned",
        "Focus on starting the ball on your intended line",
      ],
    });
  }

  // Iron play recommendations
  if (issues.includes("Iron consistency")) {
    recommendations.push({
      category: "irons",
      title: "Ball-First Contact Drill",
      description: "Practice hitting the ball first, then taking a divot",
      duration: "15 minutes",
      frequency: "Daily",
      difficulty: skillLevel,
      tips: [
        "Place a tee 6 inches in front of the ball",
        "Try to hit the ball without hitting the tee",
        "Focus on descending blow with irons",
      ],
    });
  }

  // Short game recommendations
  if (issues.includes("Short game") || handicap > 15) {
    recommendations.push({
      category: "short-game",
      title: "Distance Control Wedges",
      description:
        "Practice different length swings for precise distance control",
      duration: "25 minutes",
      frequency: "4x per week",
      difficulty: skillLevel,
      tips: [
        "Practice 1/4, 1/2, and 3/4 swings",
        "Focus on consistent tempo",
        "Track distances for each swing length",
      ],
    });
  }

  // Putting recommendations
  if (issues.includes("Putting")) {
    recommendations.push({
      category: "putting",
      title: "Gate Drill for Accuracy",
      description: "Use two tees to create a gate just wider than your putter",
      duration: "15 minutes",
      frequency: "Daily",
      difficulty: skillLevel,
      tips: [
        "Start with 3-foot putts",
        "Focus on starting the ball on line",
        "Gradually increase distance",
      ],
    });
  }

  // Mental game recommendations
  if (issues.includes("Mental game") || issues.includes("Course management")) {
    recommendations.push({
      category: "mental",
      title: "Pre-Shot Routine Development",
      description: "Develop and practice a consistent pre-shot routine",
      duration: "10 minutes",
      frequency: "Every practice session",
      difficulty: skillLevel,
      tips: [
        "Keep routine under 30 seconds",
        "Include visualization of the shot",
        "Practice routine on every shot",
      ],
    });
  }

  // Add general recommendations based on skill level
  if (skillLevel === "beginner") {
    recommendations.push({
      category: "irons",
      title: "Basic Setup Fundamentals",
      description: "Master the fundamentals of grip, stance, and posture",
      duration: "20 minutes",
      frequency: "5x per week",
      difficulty: "beginner",
      tips: [
        "Practice grip in front of a mirror",
        "Focus on athletic posture",
        "Keep practice sessions short but frequent",
      ],
    });
  } else if (skillLevel === "advanced") {
    recommendations.push({
      category: "irons",
      title: "Shot Shaping Practice",
      description: "Practice intentional draws and fades",
      duration: "30 minutes",
      frequency: "2x per week",
      difficulty: "advanced",
      tips: [
        "Adjust setup for different ball flights",
        "Practice with alignment sticks",
        "Focus on controlling curve, not distance",
      ],
    });
  }

  return recommendations;
};

export const getHandicapTrend = (
  entries: ProgressEntry[],
): "improving" | "stable" | "declining" => {
  if (entries.length < 2) return "stable";

  const recentEntries = entries
    .filter((e) => e.handicap !== undefined)
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, 3);

  if (recentEntries.length < 2) return "stable";

  const recent = recentEntries[0].handicap!;
  const older = recentEntries[recentEntries.length - 1].handicap!;

  if (recent < older - 1) return "improving";
  if (recent > older + 1) return "declining";
  return "stable";
};

export const generateProgressInsights = (
  userProfile: UserProfile,
  progressHistory: ProgressEntry[],
): string[] => {
  const insights: string[] = [];

  if (progressHistory.length === 0) {
    insights.push("Start tracking your progress to see improvements over time");
    return insights;
  }

  const trend = getHandicapTrend(progressHistory);

  if (trend === "improving") {
    insights.push("Great job! Your handicap has been improving");
  } else if (trend === "declining") {
    insights.push(
      "Your scores have been trending higher - consider focusing on fundamentals",
    );
  } else {
    insights.push("Your handicap has been stable - consistent play is good!");
  }

  // Distance tracking insights
  const distanceEntries = progressHistory.filter((e) => e.clubDistances);
  if (distanceEntries.length > 1) {
    insights.push("Keep updating your club distances as your swing improves");
  }

  // Practice recommendations based on issues
  if (userProfile.currentIssues.length > 3) {
    insights.push(
      "Focus on 1-2 areas at a time for more effective improvement",
    );
  } else if (userProfile.currentIssues.length === 0) {
    insights.push(
      "Consider identifying specific areas to work on for continued improvement",
    );
  }

  return insights;
};
